// src/pages/AdminDashboard.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import {
  Briefcase, TrendingUp, TrendingDown, Award, Clock, FileText,
  Users, Star, RefreshCw, Target, LayoutDashboard, UserCheck,
  Activity, BarChart2, PieChart as PieIcon, ShieldCheck,
  HeartHandshake, Crown, Zap, Radio,
  AlertTriangle, AlertCircle, Building2, TrendingDown as TrendDown,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { initials } from '../utils/helpers';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { kpiAPI, buAPI, companiesAPI } from '../services/api';
import './AdminDashboard.css';


// ── Palette ───────────────────────────────────────────────────
const PALETTE = {
  navy:    '#0f1726',
  blue:    '#3B82F6',
  blueSoft:'#2b49da',
  orange:  '#ff5a0a',
  orangeSoft: '#ff7d36',
  green:   '#16a34a',
  red:     '#dc2626',
  amber:   '#d97706',
  purple:  '#7c3aed',
  cyan:    '#0ea5e9',
  teal:    '#0d9488',
};

// ── Catégorisation société : client vs prospect ───────────────
// Aligné sur les statuts CRM de Companies.jsx
const COMPANY_TYPE = {
  CLIENT:                { type: 'client',   label: 'Client',         color: '#16A34A', bg: 'rgba(34,197,94,0.10)' },
  ANCIEN_CLIENT:         { type: 'client',   label: 'Ancien client',  color: '#6B7280', bg: 'rgba(107,114,128,0.10)' },
  PROSPECT:              { type: 'prospect', label: 'Prospect',       color: '#F59E0B', bg: 'rgba(245,158,11,0.10)' },
  PROSPECT_CHAUD:        { type: 'prospect', label: 'Prospect chaud', color: '#EA580C', bg: 'rgba(234,88,12,0.10)' },
  PARTENAIRE:            { type: 'autre',    label: 'Partenaire',     color: '#7c3aed', bg: 'rgba(124,58,237,0.10)' },
  ANCIEN_PARTENAIRE:     { type: 'autre',    label: 'Anc. partenaire', color: '#6B7280', bg: 'rgba(107,114,128,0.10)' },
  FOURNISSEUR:           { type: 'autre',    label: 'Fournisseur',    color: '#0ea5e9', bg: 'rgba(14,165,233,0.10)' },
  COLLABORATEUR_EXTERNE: { type: 'autre',    label: 'Collab. externe',color: '#0d9488', bg: 'rgba(13,148,136,0.10)' },
  ARCHIVE:               { type: 'autre',    label: 'Archivée',       color: '#94A3B8', bg: 'rgba(148,163,184,0.10)' },
};

const STATUS_COLORS = {
  Nouveau:      '#3B82F6',
  Qualification:'#0ea5e9',
  Sourcing:     '#ff7d36',
  Proposé:      '#7c3aed',
  Entretien:    '#0d9488',
  Gagné:        '#16a34a',
  Perdu:        '#dc2626',
  Annulé:       '#94a3b8',
};

// Clés alignées sur UserRoleEnum : le backend renvoie user.role.value, en MAJUSCULES.
const ROLE_META = {
  SUPER_ADMIN:        { label: 'Super Admin',    color: '#7c3aed', bg: '#f5f3ff' },
  ADMIN:              { label: 'Admin',          color: '#dc2626', bg: '#fff1f1' },
  BUSINESS_MANAGER:   { label: "Ing. Affaires",  color: '#3B82F6', bg: 'rgba(59,130,246,0.08)' },
  REFERENT_TECHNIQUE: { label: 'Référent Tech.', color: '#0d9488', bg: 'rgba(13,148,136,0.10)' },
  TALENT_ACQUISITION: { label: 'TA',             color: '#ff5a0a', bg: '#fff7ed' },
  RH:                 { label: 'RH',             color: '#059669', bg: '#ecfdf5' },
  COLLABORATEUR:      { label: 'Collaborateur',  color: '#0ea5e9', bg: 'rgba(14,165,233,0.10)' },
  // Alias historique
  INGENIEUR_AFFAIRES: { label: "Ing. Affaires",  color: '#3B82F6', bg: 'rgba(59,130,246,0.08)' },
};

const MONTHS_FR = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'];

// Clés alignées sur AOStatusEnum : couvre le workflow complet, pas seulement le
// pipeline simplifié — le backend renvoie ao_avancement.value brut.
const AO_STATUS_META = {
  NOUVEAU:                            { label: 'Nouveau',            color: '#3B82F6' },
  QUALIFICATION:                      { label: 'Qualification',      color: '#0ea5e9' },
  EN_COURS_DE_TRAITEMENT:             { label: 'En traitement',      color: '#0ea5e9' },
  ENVOYE_A_EQUIPE_TALENT_ACQUISITION: { label: 'Envoyé TA',          color: '#A78BFA' },
  SOURCING:                           { label: 'Sourcing',           color: '#ff7d36' },
  EN_COURS_DE_SOURCING:               { label: 'En sourcing',        color: '#ff7d36' },
  SHORT_LIST_ENVOYEE:                 { label: 'Short-list envoyée', color: '#7c3aed' },
  TOP_PROFIL_ENVOYE_AU_CLIENT:        { label: 'Top profil envoyé',  color: '#7c3aed' },
  PROPOSE:                            { label: 'Proposé',            color: '#7c3aed' },
  REPONDU:                            { label: 'Répondu',            color: '#0d9488' },
  ENTRETIEN:                          { label: 'Entretien',          color: '#0d9488' },
  GAGNE:                              { label: 'Gagné',              color: '#16a34a' },
  PERDU:                              { label: 'Perdu',              color: '#dc2626' },
  EXPIRE:                             { label: 'Expiré',             color: '#dc2626' },
  TERMINE:                            { label: 'Terminé',            color: '#64748b' },
  ABANDONNE:                          { label: 'Abandonné',          color: '#94a3b8' },
  ANNULE:                             { label: 'Annulé',             color: '#94a3b8' },
  SUPPRIME:                           { label: 'Supprimé',           color: '#94a3b8' },
};

// Dernier recours pour un code inconnu : "EN_COURS_DE_X" -> "En cours de x",
// jamais le code technique brut.
const humanizeCode = (code) =>
  String(code || '')
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/^./, c => c.toUpperCase());

const statusLabel = status => AO_STATUS_META[status]?.label || humanizeCode(status);


// ── Utilities ─────────────────────────────────────────────────
function formatMonthLabel(bucket) {
  const m = bucket?.match(/^(\d{4})-(\d{2})$/);
  if (!m) return bucket;
  return `${MONTHS_FR[parseInt(m[2]) - 1]} ${m[1].slice(2)}`;
}

function getPeriodParams(p) {
  if (p === 'all') return {};
  const to   = new Date();
  const from = new Date();
  const days = { '7d': 7, '30d': 30, '90d': 90, '1y': 365 }[p] || 0;
  from.setDate(from.getDate() - days);
  return { date_from: from.toISOString(), date_to: to.toISOString() };
}


function timeAgo(isoStr) {
  if (!isoStr) return '—';
  const diff = Date.now() - new Date(isoStr).getTime();
  const min  = Math.floor(diff / 60000);
  if (min < 1)   return "à l'instant";
  if (min < 60)  return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24)    return `il y a ${h}h`;
  const d = Math.floor(h / 24);
  return `il y a ${d}j`;
}


// ── Sub-components ────────────────────────────────────────────

function DashboardSkeleton() {
  const box = (h, w = '100%') => (
    <div className="adm-skeleton-pulse" style={{ height: h, width: w, borderRadius: 10 }} />
  );
  return (
    <div className="adm" style={{ gap: 16 }}>
      <div className="adm-skeleton-pulse" style={{ height: 82, borderRadius: 16 }} />
      <div className="adm-kpi-hero">{Array(6).fill(0).map((_,i) => <div key={i} className="adm-skeleton-pulse" style={{ height: 110, borderRadius: 14 }} />)}</div>
      <div className="adm-kpi-secondary">{Array(4).fill(0).map((_,i) => <div key={i} className="adm-skeleton-pulse" style={{ height: 72, borderRadius: 12 }} />)}</div>
      <div className="adm-charts-33-66">{box(280)}{box(280)}</div>
      <div className="adm-charts-50-50">{box(280)}{box(280)}</div>
      <div className="adm-skeleton-pulse" style={{ height: 320, borderRadius: 14 }} />
    </div>
  );
}

function EmptyChart({ label = 'Pas encore de données' }) {
  return (
    <div className="adm-empty-chart">
      <BarChart2 size={32} />
      <p>{label}</p>
    </div>
  );
}

function KPICard({ label, value, sub, icon: Icon, color, highlight }) {
  return (
    <div className={`adm-kpi-card ${highlight ? 'adm-kpi-card--highlight' : ''}`}>
      <div className="adm-kpi-card__top">
        <span className="adm-kpi-card__label">{label}</span>
        <div
          className="adm-kpi-card__icon"
          style={{
            background: highlight ? 'rgba(255,255,255,0.1)' : `${color}18`,
            border: highlight ? '1px solid rgba(255,255,255,0.12)' : `1px solid ${color}28`,
          }}
        >
          <Icon size={17} color={highlight ? '#fff' : color} />
        </div>
      </div>
      <div className="adm-kpi-card__value">{value}</div>
      <div className="adm-kpi-card__sub">{sub}</div>
      <div className="adm-kpi-card__bar" style={{ background: `linear-gradient(90deg, ${color}, transparent)` }} />
    </div>
  );
}

function KPISmall({ label, value, icon: Icon, color }) {
  return (
    <div className="adm-kpi-small">
      <div className="adm-kpi-small__icon" style={{ background: `${color}18`, border: `1px solid ${color}28` }}>
        <Icon size={18} color={color} />
      </div>
      <div className="adm-kpi-small__info">
        <div className="adm-kpi-small__val">{value}</div>
        <div className="adm-kpi-small__label">{label}</div>
      </div>
    </div>
  );
}

function PipelineFunnel({ stages }) {
  const max = stages[0]?.value || 1;
  return (
    <div className="adm-funnel">
      {stages.map((s) => {
        const pct = Math.round((s.value / max) * 100);
        return (
          <div key={s.label} className="adm-funnel-row">
            <div className="adm-funnel-label-row">
              <span className="adm-funnel-label">
                <span className="adm-funnel-dot" style={{ background: s.color }} />
                {s.label}
              </span>
              <span className="adm-funnel-values">
                <span className="adm-funnel-count">{s.value}</span>
                <span className="adm-funnel-pct">{pct}%</span>
              </span>
            </div>
            <div className="adm-funnel-track">
              <div className="adm-funnel-fill" style={{ width: `${pct}%`, background: s.color }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StatusDonut({ dist }) {
  const entries = Object.entries(dist).filter(([, v]) => v > 0);
  const total   = entries.reduce((s, [, v]) => s + v, 0);
  const pieData = entries.map(([k, v]) => ({ name: k, value: v }));
  const colors  = Object.values(STATUS_COLORS);

  return (
    <div className="adm-donut-wrap">
      <ResponsiveContainer width={160} height={160}>
        <PieChart>
          <Pie
            data={pieData} cx="50%" cy="50%"
            innerRadius={44} outerRadius={72}
            paddingAngle={2} dataKey="value"
            strokeWidth={0}
          >
            {pieData.map((_, i) => <Cell key={i} fill={colors[i % colors.length]} />)}
          </Pie>
          <Tooltip
            contentStyle={{ borderRadius: 10, border: '1px solid var(--border)', fontSize: 12 }}
            formatter={(v) => [`${v} (${Math.round(v/total*100)}%)`, '']}
          />
        </PieChart>
      </ResponsiveContainer>
      <div className="adm-donut-legend">
        {pieData.map((d, i) => (
          <div key={d.name} className="adm-legend-row">
            <span className="adm-legend-dot" style={{ background: colors[i % colors.length] }} />
            <span className="adm-legend-name">{statusLabel(d.name)}</span>
            <span className="adm-legend-val">{d.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function WinRateBadge({ won, lost }) {
  if (won + lost === 0) return <span className="adm-winrate adm-winrate--none">N/A</span>;
  const rate = Math.round((won / (won + lost)) * 100);
  const cls  = rate >= 60 ? 'adm-winrate--high' : rate >= 35 ? 'adm-winrate--mid' : 'adm-winrate--low';
  return <span className={`adm-winrate ${cls}`}>{rate}%</span>;
}

function ProgressCell({ value, max, color }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="adm-progress-cell">
      <div className="adm-progress-track">
        <div className="adm-progress-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="adm-progress-num">{value}</span>
    </div>
  );
}

function RoleBadge({ role }) {
  const key = String(role || '').toUpperCase();
  const m = ROLE_META[key] || { label: humanizeCode(key), color: '#64748b', bg: '#f1f5f9' };
  return (
    <span className="adm-role-badge" style={{ color: m.color, background: m.bg }}>
      {m.label}
    </span>
  );
}

function StatusBadge({ status }) {
  if (!status) return null;
  const key   = String(status).toUpperCase();
  const label = statusLabel(key);
  const color = AO_STATUS_META[key]?.color || '#94a3b8';
  return (
    <span
      className="adm-status-badge"
      style={{ background: `${color}18`, color, border: `1px solid ${color}30` }}
    >
      {label}
    </span>
  );
}

function ActivityPresence({ users }) {
  return (
    <div className="adm-presence-list">
      {users.map(u => {
        const diffMin = u.last_at ? Math.floor((Date.now() - new Date(u.last_at)) / 60000) : null;
        const dotCls  = diffMin === null     ? 'adm-presence-dot--off'
                      : diffMin < 15         ? 'adm-presence-dot--on'
                      : diffMin < 120        ? 'adm-presence-dot--idle'
                      : 'adm-presence-dot--off';
        return (
          <div key={u.user_id} className="adm-presence-row">
            <div className="adm-presence-left">
              <div className="adm-presence-avatar-wrap">
                <div className="adm-user-avatar" style={{ width: 32, height: 32, fontSize: '0.68rem', borderRadius: 9 }}>
                  {initials(u.user_name)}
                </div>
                <span className={`adm-presence-dot ${dotCls}`} />
              </div>
              <div>
                <div className="adm-presence-name">{u.user_name}</div>
                <RoleBadge role={u.role} />
              </div>
            </div>
            <div className="adm-presence-center">
              <span className={`adm-presence-ao${u.last_ao_title ? '' : ' adm-presence-ao--none'}`}>
                {u.last_ao_title || '—'}
              </span>
              {u.last_ao_status && <StatusBadge status={u.last_ao_status} />}
            </div>
            <div className="adm-presence-right">
              <span className="adm-presence-count">{u.count_30d}</span>
              <span className="adm-presence-count-label">30 jours</span>
            </div>
            <div className="adm-presence-time">{timeAgo(u.last_at)}</div>
          </div>
        );
      })}
    </div>
  );
}

function EventFeed({ events }) {
  return (
    <div className="adm-event-feed">
      {events.map((e, i) => (
        <div key={i} className="adm-event-row">
          <div className="adm-event-left">
            <div className="adm-user-avatar" style={{ width: 28, height: 28, fontSize: '0.6rem', borderRadius: 8, flexShrink: 0 }}>
              {initials(e.user_name)}
            </div>
            {i < events.length - 1 && <div className="adm-event-line" />}
          </div>
          <div className="adm-event-body">
            <div className="adm-event-header">
              <span className="adm-event-user">{e.user_name}</span>
              {e.ao_title && (
                <span className="adm-event-ao">{e.ao_title}</span>
              )}
            </div>
            <div className="adm-event-meta">
              {e.ao_avancement && <StatusBadge status={e.ao_avancement} />}
              <span className="adm-event-time">{timeAgo(e.at)}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function TeamLeaderboard({ metrics }) {
  const maxAO = Math.max(...metrics.map(m => m.ao_total), 1);

  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="adm-leaderboard">
        <thead>
          <tr>
            <th style={{ width: 40 }}>#</th>
            <th>Collaborateur</th>
            <th>Rôle</th>
            <th>Site</th>
            <th>Total AO</th>
            <th>Gagnés</th>
            <th>Win rate</th>
            <th>Shortlists</th>
            <th>Top profils</th>
            <th>CV convertis</th>
            <th>Tps moy (h)</th>
          </tr>
        </thead>
        <tbody>
          {metrics.map((m, i) => {
            const rankCls = i === 0 ? 'adm-rank--1' : i === 1 ? 'adm-rank--2' : i === 2 ? 'adm-rank--3' : 'adm-rank--n';
            return (
              <tr key={m.user_id}>
                <td>
                  <span className={`adm-rank ${rankCls}`}>{i + 1}</span>
                </td>
                <td>
                  <div className="adm-user-cell">
                    <div className="adm-user-avatar">{initials(m.user_name)}</div>
                    <div>
                      <div className="adm-user-name">{m.user_name}</div>
                      <div className="adm-user-site">{m.site || '—'}</div>
                    </div>
                  </div>
                </td>
                <td><RoleBadge role={m.role} /></td>
                <td style={{ fontSize: '0.78rem', color: '#64748b' }}>{m.site || '—'}</td>
                <td>
                  <ProgressCell value={m.ao_total} max={maxAO} color={PALETTE.blue} />
                </td>
                <td>
                  <span style={{ fontWeight: 700, color: PALETTE.green, fontFamily: 'var(--font-mono)' }}>
                    {m.ao_gagne}
                  </span>
                </td>
                <td><WinRateBadge won={m.ao_gagne} lost={m.ao_perdu} /></td>
                <td>
                  <ProgressCell value={m.shortlist_count} max={Math.max(...metrics.map(x => x.shortlist_count), 1)} color={PALETTE.cyan} />
                </td>
                <td>
                  <ProgressCell value={m.top_profil_count} max={Math.max(...metrics.map(x => x.top_profil_count), 1)} color={PALETTE.orange} />
                </td>
                <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, color: PALETTE.purple }}>
                  {m.converted_cv_count}
                </td>
                <td style={{ fontFamily: 'var(--font-mono)', color: '#64748b' }}>
                  {m.avg_processing_hours?.toFixed(1) ?? '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Custom tooltip for area chart
function AreaTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'rgba(9,23,56,0.96)', border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: 10, padding: '9px 14px', fontSize: 12,
    }}>
      <p style={{ color: 'rgba(255,255,255,0.55)', margin: '0 0 4px', fontSize: 11 }}>{label}</p>
      <p style={{ color: '#fff', fontWeight: 700, margin: 0 }}>
        {payload[0].value} AO
      </p>
    </div>
  );
}


const SOURCE_COLORS = {
  APEC:         '#3B82F6',
  TURNOVER_IT:  '#ff5a0a',
  LinkedIn:     '#0d9488',
  Manuel:       '#7c3aed',
  Inconnu:      '#94a3b8',
};

function CompanyTypeBadge({ status }) {
  // status absent (société pas dans le CRM) : badge gris "Inconnu"
  if (!status) {
    return (
      <span
        className="adm-company-type-badge"
        style={{ color: '#94A3B8', background: 'rgba(148,163,184,0.10)', border: '1px solid rgba(148,163,184,0.25)' }}
        title="Société non répertoriée dans le CRM"
      >
        Inconnu
      </span>
    );
  }
  const meta = COMPANY_TYPE[status] || { label: status, color: '#6B7280', bg: 'rgba(107,114,128,0.10)' };
  return (
    <span
      className="adm-company-type-badge"
      style={{ color: meta.color, background: meta.bg, border: `1px solid ${meta.color}33` }}
    >
      {meta.label}
    </span>
  );
}

function TopClientsTable({ clients, statusMap = {} }) {
  const maxTotal = Math.max(...clients.map(c => c.total), 1);
  return (
    <div className="adm-clients-list">
      {clients.slice(0, 8).map((c, i) => {
        const status = statusMap[(c.client || '').trim().toLowerCase()] || null;
        return (
          <div key={i} className="adm-client-row">
            <div className="adm-client-rank">{i + 1}</div>
            <div className="adm-client-name" title={c.client}>{c.client}</div>
            <CompanyTypeBadge status={status} />
            <div className="adm-client-bar-wrap">
              <div className="adm-client-bar-track">
                <div
                  className="adm-client-bar-fill"
                  style={{ width: `${Math.round((c.total / maxTotal) * 100)}%`, background: PALETTE.blue }}
                />
              </div>
              <span className="adm-client-count">{c.total}</span>
            </div>
            <WinRateBadge won={c.gagne} lost={c.perdu} />
          </div>
        );
      })}
    </div>
  );
}

function SourcePerf({ sources }) {
  const maxTP = Math.max(...sources.map(s => s.top_profil), 1);
  return (
    <div className="adm-source-list">
      {sources.map(s => {
        const color = SOURCE_COLORS[s.source] || PALETTE.teal;
        const tpPct = Math.round((s.top_profil / Math.max(s.total, 1)) * 100);
        return (
          <div key={s.source} className="adm-source-row">
            <div className="adm-source-dot" style={{ background: color }} />
            <div className="adm-source-name">{s.source}</div>
            <div className="adm-source-bar-track">
              <div
                className="adm-source-bar-fill"
                style={{ width: `${Math.round((s.top_profil / Math.max(maxTP, 1)) * 100)}%`, background: color }}
              />
            </div>
            <div className="adm-source-nums">
              <span className="adm-source-total">{s.total} matchs</span>
              <span className="adm-source-sl">{s.shortlist} SL</span>
              <span className="adm-source-tp" style={{ color }}>{s.top_profil} TP</span>
              <span className="adm-source-rate" style={{ color: tpPct >= 20 ? PALETTE.green : '#9aa4b8' }}>
                {tpPct}%
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StuckAOList({ items }) {
  return (
    <div className="adm-stuck-list">
      {items.map(ao => (
        <div key={ao.id} className="adm-stuck-row">
          <div
            className="adm-stuck-days"
            style={{ color: ao.days >= 14 ? PALETTE.red : PALETTE.amber }}
          >
            {ao.days}j
          </div>
          <div className="adm-stuck-info">
            <div className="adm-stuck-title">{ao.title}</div>
            <div className="adm-stuck-client">{ao.client}</div>
          </div>
          <StatusBadge status={ao.status} />
        </div>
      ))}
    </div>
  );
}


// ── Main Component ────────────────────────────────────────────
export default function AdminDashboard() {
  const { user } = useAuth();
  const [data,         setData]         = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [userId,       setUserId]       = useState('');
  const [buId,         setBuId]         = useState('');
  const [buList,       setBuList]       = useState([]);
  const [period,       setPeriod]       = useState('all');
  const [activityData, setActivityData] = useState(null);
  const [companyStatusMap, setCompanyStatusMap] = useState({}); // { 'Nom Société (normalisé)': 'CLIENT' }

  useEffect(() => {
    buAPI.list().then(r => setBuList(r.data || [])).catch(() => {});
  }, []);

  // Charge la liste des sociétés une fois pour distinguer client/prospect dans le widget "Besoins"
  useEffect(() => {
    companiesAPI.list({})
      .then(r => {
        const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        const map = {};
        list.forEach(c => {
          if (c?.name) map[c.name.trim().toLowerCase()] = c.status || null;
        });
        setCompanyStatusMap(map);
      })
      .catch(() => setCompanyStatusMap({}));
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        ...(userId ? { user_id: userId } : {}),
        ...(buId   ? { bu_id:   buId   } : {}),
        ...getPeriodParams(period),
      };
      const res = await api.get('/admin/kpi/dashboard', { params });
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [userId, buId, period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    kpiAPI.activity()
      .then(r => setActivityData(r.data))
      .catch(() => setActivityData(null));
  }, []);

  if (loading) return <DashboardSkeleton />;

  // ── Extract KPIs ───────────────────────────────────────────
  const allMetrics = data?.users_metrics || [];

  // Filtrage par BU : ne garder que les users membres de la BU sélectionnée
  const buMemberIds = buId
    ? new Set((buList.find(b => b.id === buId)?.members || []).map(m => m.user_id))
    : null;

  const metrics = buMemberIds
    ? allMetrics.filter(m => buMemberIds.has(m.user_id))
    : allMetrics;
  const gMap     = {};
  (data?.global_kpis || []).forEach(k => { gMap[k.label] = k.value; });

  const totalAO     = gMap['AO total']                        ?? 0;
  const enCours     = gMap['AO en cours']                     ?? 0;
  const aoGagne     = gMap['AO gagnés']                       ?? 0;
  const aoPerdu     = gMap['AO perdus']                       ?? 0;
  const aoAnnule    = gMap['AO annulés']                      ?? 0;
  const shortlist   = gMap['Shortlists']                      ?? 0;
  const topProfil   = gMap['Top profils']                     ?? 0;
  const avgH        = gMap['Temps moyen traitement (h)']      ?? 0;
  const cvConvertis = gMap['CV convertis']                    ?? 0;
  const winRate     = (aoGagne + aoPerdu) > 0
    ? Math.round((aoGagne / (aoGagne + aoPerdu)) * 100)
    : 0;

  // ── Chart data ─────────────────────────────────────────────
  const dailyData = (data?.ao_by_day || []).slice(-30).map(d => ({
    ...d,
    label: d.bucket ? d.bucket.slice(5) : d.bucket, // MM-DD
  }));

  const monthlyData = (data?.ao_by_month || []).slice(-12).map(d => ({
    ...d,
    label: formatMonthLabel(d.bucket),
  }));

  const weeklyData = (data?.ao_by_week || []).slice(-8).map(d => ({
    ...d,
    label: d.bucket?.replace(/^\d{4}-/, '') || d.bucket,
  }));

  const statusDist = data?.charts?.ao_status_distribution || {};
  const donutDist  = {
    Nouveau:      statusDist.NOUVEAU      || 0,
    Qualification:statusDist.QUALIFICATION|| 0,
    Sourcing:     statusDist.SOURCING     || 0,
    Proposé:      statusDist.PROPOSE      || 0,
    Entretien:    statusDist.ENTRETIEN    || 0,
    Gagné:        statusDist.GAGNE        || 0,
    Perdu:        statusDist.PERDU        || 0,
    Annulé:       statusDist.ANNULE       || 0,
  };

  const funnelStages = [
    { label: 'Total reçus',    value: totalAO,                  color: PALETTE.blue },
    { label: 'En traitement',  value: enCours,                  color: PALETTE.cyan },
    { label: 'Proposés',       value: (statusDist.PROPOSE || 0) + (statusDist.ENTRETIEN || 0), color: PALETTE.orange },
    { label: 'Gagnés',         value: aoGagne,                  color: PALETTE.green },
  ].filter(s => s.value > 0);

  // Team bar chart data (top 8)
  const teamChartData = metrics.slice(0, 8).map(m => ({
    name: m.user_name.split(' ')[0], // prénom only
    Gagnés: m.ao_gagne,
    Perdus:  m.ao_perdu,
    'En cours': m.ao_en_cours,
  }));

  // ── Backend-computed intelligence ─────────────────────────
  const stuckCount   = data?.stuck_count ?? 0;
  const stuckList    = data?.stuck_aos   ?? [];
  const topClients   = data?.top_clients ?? [];
  const sourcePerf   = data?.source_performance ?? [];
  const orphanCount  = data?.orphan_ao_count ?? 0;

  // ── Advanced computed KPIs ─────────────────────────────────
  const aoThisWeek     = weeklyData.length > 0 ? (weeklyData[weeklyData.length - 1]?.value ?? 0) : 0;
  const lastWeekVal    = weeklyData.length >= 2 ? (weeklyData[weeklyData.length - 2]?.value ?? 0) : 0;
  const growthPct      = lastWeekVal > 0
    ? Math.round(((aoThisWeek - lastWeekVal) / lastWeekVal) * 100)
    : (aoThisWeek > 0 ? 100 : null);
  const aoAdvanced     = (statusDist.PROPOSE || 0) + (statusDist.ENTRETIEN || 0) + (statusDist.GAGNE || 0);
  const conversionRate = totalAO > 0 ? Math.round((aoAdvanced / totalAO) * 100) : 0;
  const taEfficiency   = shortlist > 0 ? Math.round((topProfil / shortlist) * 100) : null;

  const PERIODS = [['7d','7 jours'], ['30d','30 jours'], ['90d','3 mois'], ['1y','1 an'], ['all','Tout']];

  // ── Render ─────────────────────────────────────────────────
  return (
    <div className="adm">

      {/* ══ HEADER ════════════════════════════════════════════ */}
      <div className="adm-header">
        <div className="adm-header-left">
          <div className="adm-header-icon"><LayoutDashboard size={20} /></div>
          <div>
            <h1 className="adm-title">Dashboard</h1>
            <p className="adm-sub">
              {buId ? buList.find(b => b.id === buId)?.name : (user?.site || 'Toutes les BUs')}
              {userId && metrics.length > 0
                ? ` · ${metrics.find(m => m.user_id === userId)?.user_name || ''}`
                : buId ? ` · ${metrics.length} membre${metrics.length > 1 ? 's' : ''}` : ' · Vue globale'}
            </p>
          </div>
        </div>
        <div className="adm-header-right">
          <div className="adm-period-tabs">
            {PERIODS.map(([key, label]) => (
              <button
                key={key}
                className={`adm-period-btn ${period === key ? 'active' : ''}`}
                onClick={() => setPeriod(key)}
              >
                {label}
              </button>
            ))}
          </div>
          {buList.length > 0 && (
            <select
              className="filter-select"
              value={buId}
              onChange={e => { setBuId(e.target.value); setUserId(''); }}
            >
              <option value="">Toutes les BUs</option>
              {buList.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          )}
          {metrics.length > 0 && (
            <select
              className="filter-select"
              value={userId}
              onChange={e => setUserId(e.target.value)}
            >
              <option value="">Toute l'équipe</option>
              {metrics.map(m => (
                <option key={m.user_id} value={m.user_id}>{m.user_name}</option>
              ))}
            </select>
          )}
          <button className="btn btn-secondary" onClick={fetchData} title="Actualiser">
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {/* ══ ALERTES PIPELINE ═════════════════════════════════ */}
      {(stuckCount > 0 || orphanCount > 0) && (
        <div className="adm-alerts">
          {stuckCount > 0 && (
            <div className="adm-alert adm-alert--danger">
              <AlertTriangle size={14} />
              <strong>{stuckCount}</strong> AO{stuckCount > 1 ? 's' : ''} bloqué{stuckCount > 1 ? 's' : ''} sans activité depuis plus de 7 jours
            </div>
          )}
          {orphanCount > 0 && (
            <div className="adm-alert adm-alert--warning">
              <AlertCircle size={14} />
              <strong>{orphanCount}</strong> AO{orphanCount > 1 ? 's' : ''} actif{orphanCount > 1 ? 's' : ''} sans responsable assigné
            </div>
          )}
        </div>
      )}

      {/* ══ KPI HERO ROW ══════════════════════════════════════ */}
      <div className="adm-kpi-hero">
        <KPICard
          label="Total AO" value={totalAO}
          sub={`${enCours} en cours actuellement`}
          icon={Briefcase} color={PALETTE.blue}
        />
        <KPICard
          label="AO en cours" value={enCours}
          sub={`${totalAO > 0 ? Math.round(enCours/totalAO*100) : 0}% du portefeuille actif`}
          icon={Activity} color={PALETTE.cyan}
        />
        <KPICard
          label="AO gagnés" value={aoGagne}
          sub="Affaires conclues"
          icon={Award} color={PALETTE.green}
        />
        <KPICard
          label="AO perdus" value={aoPerdu}
          sub={aoAnnule > 0 ? `+ ${aoAnnule} annulés / terminés` : 'Affaires non remportées'}
          icon={TrendingDown} color={PALETTE.red}
        />
        <KPICard
          label="Taux de réussite" value={`${winRate}%`}
          sub={`${aoGagne} gagnés / ${aoPerdu} perdus`}
          icon={Target}
          color={winRate >= 60 ? PALETTE.green : winRate >= 35 ? PALETTE.amber : PALETTE.red}
          highlight
        />
        <KPICard
          label="Temps moyen" value={`${Number(avgH).toFixed(1)}h`}
          sub="Par AO traité"
          icon={Clock} color={PALETTE.purple}
        />
      </div>

      {/* ══ KPI SECONDARY ═════════════════════════════════════ */}
      <div className="adm-kpi-secondary">
        <KPISmall label="Shortlists envoyées"  value={shortlist}        icon={Users}       color={PALETTE.cyan}   />
        <KPISmall label="Top profils envoyés"  value={topProfil}        icon={Star}        color={PALETTE.orange} />
        <KPISmall label="CV convertis"         value={cvConvertis}      icon={FileText}    color={PALETTE.purple} />
        <KPISmall label="Collaborateurs actifs" value={metrics.length}  icon={UserCheck}   color={PALETTE.teal}   />
      </div>

      {/* ══ ADVANCED KPIs ════════════════════════════════════ */}
      <div className="adm-kpi-advanced">
        <KPICard
          label="AO cette semaine"
          value={aoThisWeek}
          sub={
            growthPct !== null
              ? `${growthPct >= 0 ? '+' : ''}${growthPct}% vs semaine précédente`
              : 'Nouvelles entrées cette semaine'
          }
          icon={growthPct !== null && growthPct < 0 ? TrendDown : Zap}
          color={growthPct !== null && growthPct < 0 ? PALETTE.red : PALETTE.amber}
        />
        <KPICard
          label="Taux conversion pipeline"
          value={`${conversionRate}%`}
          sub={`${aoAdvanced} AO en phase avancée (proposé → gagné)`}
          icon={TrendingUp} color={PALETTE.teal}
        />
        <KPICard
          label="Efficacité sourcing TA"
          value={taEfficiency !== null ? `${taEfficiency}%` : 'N/A'}
          sub="Top profils remontés / shortlists envoyées"
          icon={HeartHandshake} color={PALETTE.purple}
        />
        <KPICard
          label="AOs bloqués"
          value={stuckCount}
          sub={stuckCount > 0 ? `Sans activité depuis +7j — intervention requise` : 'Pipeline fluide'}
          icon={AlertTriangle}
          color={stuckCount === 0 ? PALETTE.green : stuckCount <= 3 ? PALETTE.amber : PALETTE.red}
        />
        <KPICard
          label="AOs orphelins"
          value={orphanCount}
          sub={orphanCount > 0 ? 'Aucun responsable assigné' : 'Tous les AOs sont assignés'}
          icon={AlertCircle}
          color={orphanCount === 0 ? PALETTE.green : PALETTE.amber}
        />
        <KPICard
          label="Top clients actifs"
          value={topClients.length}
          sub={topClients[0] ? `1er : ${topClients[0].client} (${topClients[0].total} AO)` : 'Pas encore de données'}
          icon={Building2} color={PALETTE.blue}
        />
      </div>

      {/* ══ ACTIVITÉ JOURNALIÈRE ══════════════════════════════ */}
      {dailyData.length > 0 && (
        <div className="adm-card">
          <div className="adm-card-header">
            <span className="adm-card-title"><Activity size={14}/> Activité journalière — AOs créés (30 derniers jours)</span>
            <span className="adm-card-sub">
              {dailyData.reduce((s, d) => s + d.value, 0)} AOs sur la période
            </span>
          </div>
          <div style={{ padding:'8px 8px 0' }}>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={dailyData} margin={{ top:4, right:8, bottom:0, left:-20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" vertical={false}/>
                <XAxis dataKey="label" tick={{ fontSize:10, fill:'#94a3b8' }} interval={Math.floor(dailyData.length / 10)} axisLine={false} tickLine={false}/>
                <YAxis tick={{ fontSize:10, fill:'#94a3b8' }} axisLine={false} tickLine={false} allowDecimals={false}/>
                <Tooltip
                  contentStyle={{ background:'var(--bg-card)', border:'1px solid #e2e8f0', borderRadius:8, fontSize:12 }}
                  formatter={(v) => [`${v} AO`, 'Créés']}
                  labelFormatter={(l) => `Jour ${l}`}
                />
                <Bar dataKey="value" fill={PALETTE.blue} radius={[4,4,0,0]} maxBarSize={18}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ══ ACTIVITÉ MENSUELLE + FUNNEL ═══════════════════════ */}
      <div className="adm-charts-33-66">

        {/* Funnel commercial */}
        <div className="adm-card">
          <div className="adm-card-header">
            <span className="adm-card-title"><Target size={14} /> Funnel commercial</span>
            <span className="adm-card-sub">{totalAO} AO au total</span>
          </div>
          {funnelStages.length > 0
            ? <PipelineFunnel stages={funnelStages} />
            : <EmptyChart />}
        </div>

        {/* Area chart mensuel */}
        <div className="adm-card">
          <div className="adm-card-header">
            <span className="adm-card-title"><TrendingUp size={14} /> Activité mensuelle</span>
            {monthlyData.length > 0 && (
              <span className="adm-card-sub">
                {monthlyData[0]?.label} → {monthlyData[monthlyData.length - 1]?.label}
              </span>
            )}
          </div>
          <div style={{ padding: '8px 8px 0' }}>
            {monthlyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={monthlyData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gradBlue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor={PALETTE.blue} stopOpacity={0.2} />
                      <stop offset="95%" stopColor={PALETTE.blue} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f3f9" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#9aa4b8' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#9aa4b8' }} axisLine={false} tickLine={false} />
                  <Tooltip content={<AreaTooltip />} />
                  <Area
                    type="monotone" dataKey="value" name="AO"
                    stroke={PALETTE.blue} strokeWidth={2.5}
                    fill="url(#gradBlue)"
                    dot={{ r: 3.5, fill: PALETTE.blue, strokeWidth: 2, stroke: '#fff' }}
                    activeDot={{ r: 5, fill: PALETTE.blue, stroke: '#fff', strokeWidth: 2 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : <EmptyChart />}
          </div>
        </div>
      </div>

      {/* ══ PERF ÉQUIPE + DONUT STATUTS ═══════════════════════ */}
      <div className="adm-charts-50-50">

        {/* Bar chart équipe */}
        <div className="adm-card">
          <div className="adm-card-header">
            <span className="adm-card-title"><BarChart2 size={14} /> Performance équipe</span>
            <span className="adm-card-sub">Top {Math.min(metrics.length, 8)} collaborateurs</span>
          </div>
          <div style={{ padding: '8px 4px 0' }}>
            {teamChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={230}>
                <BarChart data={teamChartData} layout="vertical" barSize={10} barCategoryGap="28%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f3f9" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#9aa4b8' }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} width={72} />
                  <Tooltip
                    contentStyle={{ borderRadius: 10, border: '1px solid var(--border)', fontSize: 12 }}
                    cursor={{ fill: '#f8fafc' }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                  <Bar dataKey="Gagnés"    fill={PALETTE.green}  radius={[0, 4, 4, 0]} />
                  <Bar dataKey="En cours"  fill={PALETTE.blue}   radius={[0, 4, 4, 0]} />
                  <Bar dataKey="Perdus"    fill={PALETTE.red}    radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <EmptyChart />}
          </div>
        </div>

        {/* Donut statuts */}
        <div className="adm-card">
          <div className="adm-card-header">
            <span className="adm-card-title"><PieIcon size={14} /> Répartition des statuts</span>
            <span className="adm-card-sub">{totalAO} AO analysés</span>
          </div>
          {Object.values(donutDist).some(v => v > 0)
            ? <StatusDonut dist={donutDist} />
            : <EmptyChart />}
        </div>
      </div>

      {/* ══ ACTIVITÉ HEBDO ════════════════════════════════════ */}
      {weeklyData.length > 0 && (
        <div className="adm-card">
          <div className="adm-card-header">
            <span className="adm-card-title"><Activity size={14} /> AO traités par semaine</span>
            <span className="adm-card-sub">8 dernières semaines</span>
          </div>
          <div style={{ padding: '8px 8px 0' }}>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={weeklyData} barSize={32}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f3f9" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#9aa4b8' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#9aa4b8' }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ borderRadius: 10, border: '1px solid var(--border)', fontSize: 12 }}
                  cursor={{ fill: '#f8fafc' }}
                />
                <Bar dataKey="value" name="AO" radius={[5, 5, 0, 0]}>
                  {weeklyData.map((_, i) => (
                    <Cell
                      key={i}
                      fill={i === weeklyData.length - 1 ? PALETTE.orange : PALETTE.blue}
                      opacity={i === weeklyData.length - 1 ? 1 : 0.55 + (i / weeklyData.length) * 0.4}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ══ LEADERBOARD ═══════════════════════════════════════ */}
      {metrics.length > 0 && (
        <div className="adm-card">
          <div className="adm-card-header">
            <span className="adm-card-title">
              <ShieldCheck size={14} /> Classement équipe
            </span>
            <span className="adm-card-sub">{metrics.length} collaborateur{metrics.length > 1 ? 's' : ''}</span>
          </div>
          <TeamLeaderboard metrics={metrics} />
        </div>
      )}

      {/* ══ INTELLIGENCE COMMERCIALE ═════════════════════════ */}
      <div className="adm-charts-33-66">

        {/* Besoins par client / prospect */}
        <div className="adm-card">
          <div className="adm-card-header">
            <span className="adm-card-title"><Building2 size={14} /> Besoins par client / prospect</span>
            <span className="adm-card-sub">
              {topClients.length} société{topClients.length > 1 ? 's' : ''} avec besoins actifs
            </span>
          </div>
          {topClients.length > 0
            ? <TopClientsTable clients={topClients} statusMap={companyStatusMap} />
            : <EmptyChart label="Aucun besoin enregistré pour client ou prospect" />}
        </div>

        {/* Source candidats */}
        <div className="adm-card">
          <div className="adm-card-header">
            <span className="adm-card-title"><Target size={14} /> Performance par source</span>
            <span className="adm-card-sub">APEC · LinkedIn · TurnoverIT · Manuel</span>
          </div>
          {sourcePerf.length > 0
            ? <SourcePerf sources={sourcePerf} />
            : <EmptyChart label="Aucun match candidat enregistré" />}
        </div>

      </div>

      {/* AOs bloqués — liste détaillée */}
      {stuckList.length > 0 && (
        <div className="adm-card adm-card--alert">
          <div className="adm-card-header">
            <span className="adm-card-title" style={{ color: PALETTE.red }}>
              <AlertTriangle size={14} color={PALETTE.red} /> AOs bloqués — sans activité
            </span>
            <span className="adm-card-sub">{stuckList.length} AO{stuckList.length > 1 ? 's' : ''} nécessitent une intervention</span>
          </div>
          <StuckAOList items={stuckList} />
        </div>
      )}

      {/* ══ ACTIVITÉ ÉQUIPE ═══════════════════════════════════ */}
      {activityData && (
        <div className="adm-activity-grid">

          {/* Présence et suivi */}
          <div className="adm-card">
            <div className="adm-card-header">
              <span className="adm-card-title"><Radio size={14} /> Présence équipe</span>
              <span className="adm-card-sub">
                {activityData.users_activity?.length || 0} membres · mise à jour en temps réel
              </span>
            </div>
            {activityData.users_activity?.length > 0
              ? <ActivityPresence users={activityData.users_activity} />
              : <EmptyChart label="Aucune activité enregistrée" />}
          </div>

          {/* Fil d'activité global */}
          <div className="adm-card">
            <div className="adm-card-header">
              <span className="adm-card-title"><Zap size={14} /> Fil d'activité</span>
              <span className="adm-card-sub">
                {activityData.recent_events?.length || 0} derniers événements
              </span>
            </div>
            {activityData.recent_events?.length > 0
              ? <EventFeed events={activityData.recent_events} />
              : <EmptyChart label="Aucun événement récent" />}
          </div>

        </div>
      )}

    </div>
  );
}
